import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory, type Article } from '@/mocks/articles';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';

const articleImageMap: Record<string, string> = {
  'article-newbie-01': 'https://readdy.ai/api/search-image?query=A%20warm%20minimalist%20flat%20lay%20of%20a%20smartphone%20displaying%20photo%20editing%20app%20interface%20on%20clean%20desk%20with%20soft%20natural%20light%2C%20creative%20workspace%20vibe%2C%20editorial%20photography%20style%20with%20gentle%20shadows%20and%20creamy%20beige%20background&width=800&height=480&seq=article-newbie-01&orientation=landscape',
  'article-update-02': 'https://readdy.ai/api/search-image?query=A%20modern%20smartphone%20floating%20with%20glowing%20AI%20circuit%20patterns%20around%20it%20on%20a%20warm%20gradient%20background%20of%20soft%20yellow%20and%20cream%20tones%2C%20futuristic%20yet%20approachable%20tech%20aesthetic%2C%20clean%20composition%20with%20subtle%20shadows&width=800&height=480&seq=article-update-02&orientation=landscape',
  'article-filter-03': 'https://readdy.ai/api/search-image?query=A%20beautiful%20flat%20lay%20of%20color%20palette%20swatches%20and%20a%20phone%20showing%20photo%20editing%20app%20on%20a%20warm%20cream%20desk%20surface%2C%20creative%20studio%20atmosphere%20with%20soft%20natural%20side%20lighting%2C%20artistic%20composition&width=800&height=480&seq=article-filter-03&orientation=landscape',
  'article-compare-04': 'https://readdy.ai/api/search-image?query=A%20side%20by%20side%20comparison%20of%20two%20smartphones%20displaying%20different%20photo%20editing%20app%20results%20on%20a%20clean%20minimalist%20desk%2C%20warm%20natural%20lighting%2C%20editorial%20product%20comparison%20style%20with%20soft%20shadows&width=800&height=480&seq=article-compare-04&orientation=landscape',
  'article-typography-05': 'https://readdy.ai/api/search-image?query=A%20creative%20flat%20lay%20of%20typography%20samples%20and%20a%20phone%20displaying%20text%20layout%20design%20on%20a%20warm%20beige%20background%2C%20editorial%20design%20vibe%20with%20soft%20natural%20light%2C%20clean%20and%20artistic%20composition&width=800&height=480&seq=article-typography-05&orientation=landscape',
  'article-android-06': 'https://readdy.ai/api/search-image?query=An%20Android%20smartphone%20displaying%20app%20download%20page%20on%20screen%20placed%20on%20a%20warm%20wooden%20desk%20with%20soft%20natural%20light%2C%20clean%20minimalist%20tech%20lifestyle%20photography%2C%20creamy%20background%20tones&width=800&height=480&seq=article-android-06&orientation=landscape',
  'article-template-07': 'https://readdy.ai/api/search-image?query=A%20creative%20workspace%20with%20a%20phone%20showing%20design%20template%20layout%2C%20surrounded%20by%20design%20sketches%20and%20color%20swatches%20on%20a%20warm%20cream%20desk%2C%20artistic%20studio%20atmosphere%20with%20soft%20morning%20light%2C%20clean%20composition&width=800&height=480&seq=article-template-07&orientation=landscape',
  'article-faq-08': 'https://readdy.ai/api/search-image?query=A%20minimalist%20flat%20lay%20of%20a%20phone%20displaying%20help%20and%20support%20screen%20with%20a%20small%20plant%20and%20coffee%20cup%20on%20a%20warm%20beige%20desk%2C%20soft%20natural%20light%20from%20window%2C%20calm%20and%20helpful%20atmosphere&width=800&height=480&seq=article-faq-08&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'shiyong-jiaocheng');
  const [articles, setArticles] = useState<Article[]>([]);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    setArticles(getArticlesByCategory(activeCategory));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory]);

  const currentCategory = getCategoryBySlug(activeCategory);

  return (
    <>
      <SEOHead
        title={`${currentCategory?.name || '文章分类'} - 黄油下载`}
        description={currentCategory?.description || '黄油相机相关文章分类'}
        keywords={`黄油下载,黄油相机,${currentCategory?.name || ''}`}
        canonicalUrl={`https://jiruida.cn/category/${activeCategory}`}
      />

      <SchemaOrg
        type="WebPage"
        data={{
          name: `${currentCategory?.name || ''} - 黄油下载`,
          url: `https://jiruida.cn/category/${activeCategory}`,
          description: currentCategory?.description || '',
        }}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: currentCategory?.name || '文章分类' },
          ]}
        />

        <div className="mt-4 mb-10">
          <h1 className="text-3xl md:text-4xl font-heading text-foreground-900 mb-3">
            {currentCategory?.name || '文章分类'}
          </h1>
          <p className="text-sm text-foreground-500">{currentCategory?.description}</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              onClick={() => setActiveCategory(cat.slug)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                activeCategory === cat.slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className={`${cat.icon} mr-1.5`}></i>
              {cat.name}
            </Link>
          ))}
        </div>

        {articles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {articles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={articleImageMap[article.imageSeq] || articleImageMap['article-newbie-01']}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-foreground-400">{article.date}</span>
                    <span className="text-xs text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full">{article.tags[0]}</span>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1.5 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                    {article.title}
                  </h3>
                  <p className="text-xs text-foreground-500 line-clamp-2">{article.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-2xl">
              <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500 text-sm">该分类暂无文章</p>
          </div>
        )}
      </div>
    </>
  );
}